import React, { useState } from "react";
import { createUseStyles } from "react-jss";
import GenericBasicInfo from "../../../../../components/cards/GenericBasicInfo";
import {
  cardStyles,
  modalGenericStyles,
  variables,
} from "../../../../../variables";
import PropTypes from "prop-types";
import _ from "lodash";
import { Price } from "../../../../../components/Price";
import { DateTime } from "luxon";
import GuestCard from "../../../../../components/cards/GuestInfo";
import useReservationDetails from "../../../../../hooks";
import EditGuestModal from "../../../CommonCRSModals/EditGuestModal";
import EmailsCard from "../../../../../components/cards/emails";
import Loader from "../../../../../components/Loader";
import EditCRSTagModal from "../../../CommonCRSModals/EditCRSTagModal/index";
import { toast } from "react-toastify";
import ReservationEventsModal from "../../../ReservationEventsModal";
import { DocumentsCRSCard } from "../../../CommonCRSModals/DocumentsCRS/List";
import {
  patchTrainApiReservation,
  retrieveTrainApiReservation,
} from "../../../../../api/CRS/ApiTrains";
import Leg from "../../Components/Legs";
import ReservationsStatusTag from "../../../../../components/Tags/ReservationsStatusTag";
import TabStrip from "../../../../../components/TabStrip";
import { CustomButton } from "../../../../../components/CustomButton";
import ApiTrainsLogsTab from "./ApiTrainsLogsTab";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { genericRequestErrorHandler } from "../../../../../api";
import Receivables from "../../../CommonCRSModals/Receivables";
import { InternalLogs } from "../../../CommonCRSModals/InternalLogs";

const styles = createUseStyles({
  ...modalGenericStyles,
  DetailApiTrainModal: {
    ...modalGenericStyles.modal,
  },
  modalCard: {
    ...modalGenericStyles.card,
    gridTemplateRows: "max-content max-content 1fr max-content",
  },
  modalCardHeader: {
    ...modalGenericStyles.cardHeader,
  },
  modalCardBody: {
    ...modalGenericStyles.cardBody,
    display: "grid",
    gridTemplateRows: "max-content max-content 1fr max-content",
    padding: "",
    overflowX: "hidden",
  },
  modalCardActions: {
    ...modalGenericStyles.cardActions,
  },
  card: { ...cardStyles.card, maxWidth: "100%" },
  cardHeader: cardStyles.header,
  cardBody: {
    ...cardStyles.body,
    gap: variables.normal_gap,
    maxWidth: "100%",
    overflow: "auto",
    justifyContent: "start",
  },
  firstRow: {
    display: "grid",
    gridTemplateColumns: "repeat(1, 1fr)",
    padding: variables.normal_gap,
    gap: variables.normal_gap,
  },
  creatorCardBody: {
    extend: "cardBody",
    padding: [`calc(${variables.normal_gap} * 3.3)`, variables.normal_gap],
  },
  componentRow: {
    extend: "firstRow",
    gridTemplateColumns: "repeat(1, 1fr)",
  },
  [`@media ${variables.media.smallscreen}`]: {
    modalCardBody: {
      minWidth: "60rem",
    },
    firstRow: {
      gridTemplateColumns: "1fr 1fr",
      justifyItems: "center",
    },
    creatorCardBody: {
      padding: [`calc(${variables.normal_gap} * 3)`, 0],
    },
  },
});
const DetailApiTrainModal = ({ reference, onClose }) => {
  const classes = styles();

  const [activeTab, setActiveTab] = useState("basicInfo");

  const queryClient = useQueryClient();

  const menuTabs = {
    basicInfo: "Basic Info",
    reservationEvents: "Reservation Events",
    receivables: "Receivables",
    documents: "Documents",
    internalLogs: "Internal Logs",
    logs: "User Action Logs",
  };

  const {
    data,
    guestEditMode,
    guestEditId,
    editCRSTagMode,
    reservationEventsModal,
    openGuestModal,
    closeGuestModal,
    openEditCRSTagModal,
    closeEditCRSTagModal,
    closeReservationEventsModal,
    // loading,
  } = useReservationDetails({
    reference,
    retrieveReservationData: retrieveTrainApiReservation,
    onCloseDetails: onClose,
  });

  const apiTrain = data;
  const legs = _.get(
    apiTrain,
    "data.service_data.raw_data.bookingItems.0.legs",
    []
  );

  const basicDataCpl = [
    ["Reference", "reference"],
    ["Supplier Reference", "supplier_reference"],
    ["Adults", "adults"],
    [
      "Children",
      null,
      _.get(apiTrain, "data.children") !== 0
        ? _.get(apiTrain, "children")
          ? _.get(apiTrain, "children")
          : null
        : "0",
    ],
    [
      "Original Service Price",
      null,
      <Price
        currency={_.get(apiTrain, "currency")}
        price={_.get(apiTrain, "original_service_price")}
      />,
    ],
    ["Payment Mode", "payment_mode_display"],
    [
      "Ticket Classes",
      null,
      _.get(apiTrain, "data.ticket_classes", [])
        .map((tc) => _.startCase(tc))
        .join(","),
    ],
    ["Market Type", "market_type"],
    ["Source Entity", "source_entity"],
    [
      "Created",
      null,
      DateTime.fromISO(_.get(apiTrain, "data.created")).toLocaleString(
        DateTime.DATE_MED_WITH_WEEKDAY
      ),
    ],
    [
      "Status",
      null,
      <ReservationsStatusTag status={_.get(apiTrain, "data.status")} />,
    ],
  ];
  const creatorDataCpl = [
    ["External Creator", "external_creator"],
    [
      "Creator",
      null,
      _.get(apiTrain, "data.guests", []).map((des) => _.get(des, "creator")),
    ],
  ];

  const { mutate: onSubmitTags, isLoading: tagsLoading } = useMutation({
    mutationFn: (values) => {
      return patchTrainApiReservation({ payload: values, reference });
    },
    onSuccess: () => {
      toast.success("Tags updated successfully");
      queryClient.invalidateQueries("apiTrains");
      onClose();
    },
    onError: (error) => {
      genericRequestErrorHandler(error);
    },
  });

  return (
    <div id="DetailAPITRAModal" className={classes.DetailApiTrainModal}>
      <div className={classes.modalCard}>
        <div className={classes.cardHeader}>
          <span>Api Train Details</span>
        </div>
        <TabStrip
          tabs={Object.entries(menuTabs)}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
        <div className={classes.modalCardBody}>
          {_.isEmpty(apiTrain) ? (
            <Loader />
          ) : (
            <React.Fragment>
              {activeTab === "basicInfo" && (
                <React.Fragment>
                  <div className={classes.firstRow}>
                    <div className={classes.cardBody}>
                      <GenericBasicInfo
                        actions={[["Edit", () => openEditCRSTagModal()]]}
                        header={"Basic Data"}
                        cardInfo={basicDataCpl}
                        objectData={_.get(apiTrain, "data")}
                      />
                    </div>
                    <div className={classes.creatorCardBody}>
                      <GenericBasicInfo
                        header="Creator Data"
                        cardInfo={creatorDataCpl}
                        objectData={_.get(apiTrain, "data")}
                      />
                    </div>
                  </div>
                  {/* <div className={classes.firstRow}> */}
                  <div className={classes.cardBody}>
                    {legs.map((leg, idx) => (
                      <Leg legIdx={idx} reservation={_.get(apiTrain, "data")} />
                    ))}
                    {/* </div> */}
                  </div>
                  <div className={classes.componentRow}>
                    <div className={classes.cardBody}>
                      <GuestCard
                        title={"Guest Informations"}
                        guests={_.get(apiTrain, "data.guests", [])}
                        onEditGuest={(id) => {
                          openGuestModal(id);
                        }}
                        onCloseEdit={() => closeGuestModal()}
                      />
                      <EmailsCard
                        title={"Email Informations"}
                        market_type={_.get(apiTrain, "data.market_type", "")}
                        reservation_id={_.get(apiTrain, "data.reference", "")}
                        service_type="TRA"
                        status={_.get(apiTrain, "data.status", "")}
                      />
                    </div>
                  </div>
                </React.Fragment>
              )}
              {activeTab === "reservationEvents" && (
                <ReservationEventsModal
                  header="Reservation Events"
                  onClose={() => closeReservationEventsModal()}
                  events={_.get(apiTrain, "data.reservation_events", [])}
                />
              )}
              {guestEditMode && !reservationEventsModal && !editCRSTagMode && (
                <EditGuestModal
                  onClose={closeGuestModal}
                  guestId={guestEditId}
                  queryKey={`accommodation_reservation_users_documents`}
                  reference={_.get(apiTrain, "data.reference")}
                  sourceEntity={_.get(apiTrain, "data.source_entity", "")}
                />
              )}
              {!guestEditMode && !reservationEventsModal && editCRSTagMode && (
                <EditCRSTagModal
                  header={"API Trains"}
                  tagsLoading={tagsLoading}
                  tags={_.get(apiTrain, "data.tags")}
                  onClose={closeEditCRSTagModal}
                  onSubmitTags={onSubmitTags}
                />
              )}
              {activeTab === "receivables" && (
                <Receivables data={_.get(apiTrain, "data.payment_links", [])} />
              )}
              {activeTab === "documents" && (
                <DocumentsCRSCard
                  title="Documents"
                  queryKey={`apiTrains_reservation_document`}
                  showAddButton={true}
                  tags={[
                    `${_.get(apiTrain, "data.reference")}_reservation_TRA`,
                  ]}
                  reference={_.get(apiTrain, "data.reference")}
                  sourceEntity={_.get(apiTrain, "data.source_entity", "")}
                />
              )}
              {activeTab === "internalLogs" && (
                <InternalLogs
                  prebookData={_.get(apiTrain, "data.prebook_data", {})}
                  bookData={_.get(apiTrain, "data.post_booking_data", {})}
                />
              )}
              {activeTab === "logs" && <ApiTrainsLogsTab />}
            </React.Fragment>
          )}
        </div>
        <div className={classes.cardActions}>
          <CustomButton id="cancel" appearance="ghost" onClick={onClose}>
            <strong>Cancel</strong>
          </CustomButton>
        </div>
      </div>
    </div>
  );
};
DetailApiTrainModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};
export default DetailApiTrainModal;
